import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { usePageMeta } from "@/hooks/usePageMeta";
import ArticleCard from "@/components/base/ArticleCard";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilterSidebar, { type FilterState } from "@/pages/category/components/FilterSidebar";

const PAGE_SIZE = 6;

const timeMs: Record<string, number> = {
  "1d": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};

export default function CategoryPage() {
  const { categoryName } = useParams();
  const category = categories.find((c) => c.slug === categoryName);

  const [filters, setFilters] = useState<FilterState>({ sub: "", time: "all", type: "all" });
  const [sort, setSort] = useState<"time" | "hot">("time");
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  usePageMeta({
    title: category ? `${category.name} - 吃瓜网站` : "分类未找到 - 吃瓜网站",
    description: category
      ? `${category.desc} 吃瓜网站为你聚合${category.name}的最新资讯与热门八卦。`
      : "该分类暂不存在，返回吃瓜网站首页查看最新娱乐资讯。",
    keywords: category ? `${category.name},${category.subcategories.join(",")},吃瓜网站,娱乐资讯` : "吃瓜网站,娱乐资讯",
  });

  const list = useMemo(() => {
    if (!category) return [];
    let result = articles.filter((a) => a.category === category.slug);
    if (filters.sub) result = result.filter((a) => a.tags.includes(filters.sub));
    if (filters.type !== "all") result = result.filter((a) => a.type === filters.type);
    if (filters.time !== "all") {
      const limit = timeMs[filters.time];
      const now = Date.now();
      result = result.filter((a) => now - new Date(a.publishTime).getTime() <= limit);
    }
    result = [...result].sort((a, b) =>
      sort === "hot"
        ? b.views - a.views
        : new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime(),
    );
    return result;
  }, [category, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = list.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (!category) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <i className="ri-emotion-sad-line text-6xl text-foreground-300" />
        <h2 className="mt-4 text-xl font-bold text-foreground-900">分类不存在</h2>
        <p className="mt-2 text-sm text-foreground-500">你访问的分类可能已被移除，去看看其他内容吧。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 px-5 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap transition-colors"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const updateFilters = (next: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ sub: "", time: "all", type: "all" });
    setPage(1);
  };

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "分类" }, { label: category.name }]} />

      <div className="mt-4 flex items-center gap-4">
        <span className="w-14 h-14 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
          <i className={`${category.icon} text-3xl`} />
        </span>
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-950">{category.name}</h1>
          <p className="mt-1 text-sm text-foreground-500 max-w-2xl">{category.desc}</p>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
        <aside className="hidden lg:block lg:col-span-1">
          <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-5">
            <FilterSidebar category={category} filters={filters} onChange={updateFilters} onReset={resetFilters} />
          </div>
        </aside>

        <div className="lg:col-span-3">
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="text-sm text-foreground-500">
              共 <span className="text-primary-600 font-semibold">{list.length}</span> 条资讯
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMobileFilterOpen(true)}
                className="lg:hidden inline-flex items-center gap-1 h-9 px-3 rounded-md border border-background-200 text-sm text-foreground-600 bg-background-50 whitespace-nowrap"
              >
                <i className="ri-filter-3-line" />
                筛选
              </button>
              <div className="flex items-center bg-background-100 rounded-full p-1">
                <button
                  type="button"
                  onClick={() => setSort("time")}
                  className={`px-3 py-1 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors ${
                    sort === "time" ? "bg-background-50 text-primary-600 font-semibold" : "text-foreground-500"
                  }`}
                >
                  最新
                </button>
                <button
                  type="button"
                  onClick={() => setSort("hot")}
                  className={`px-3 py-1 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors ${
                    sort === "hot" ? "bg-background-50 text-primary-600 font-semibold" : "text-foreground-500"
                  }`}
                >
                  最热
                </button>
              </div>
            </div>
          </div>

          {paged.length > 0 ? (
            <>
              <div className="space-y-3 md:space-y-4">
                {paged.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
              <Pagination current={currentPage} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="py-16 text-center rounded-lg border border-dashed border-background-300 bg-background-50">
              <i className="ri-inbox-line text-5xl text-foreground-300" />
              <h3 className="mt-4 text-lg font-bold text-foreground-900">没有找到符合条件的内容</h3>
              <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或重置后重新查看。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-2 mt-5 px-5 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap transition-colors"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>

      {mobileFilterOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileFilterOpen(false)} />
          <div className="relative w-full bg-background-50 rounded-t-2xl p-5 max-h-[80vh] overflow-y-auto animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterSidebar category={category} filters={filters} onChange={updateFilters} onReset={resetFilters} />
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="mt-5 w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap"
            >
              查看结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}